import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Select } from "@legal-500/legal500-design-system";
import { researchCms } from "@/api/researchCms.service";
import {
  mergeAddCandidates,
  mergeAddSourceIds,
  type MergeCard,
  type MergeRow,
} from "../../utils/bpaMerge";
import { messageOf } from "../BpaImport/importText";
import { splitOpenQuery } from "./CmsBpaMergeDialog";
import styles from "./CmsBpaMergeDialog.module.css";
import shared from "../BpaImport/importShared.module.css";

const PICKER_PLACEHOLDER = "Choose a practice area in this edition…";

type CmsBpaMergeAddDialogProps = {
  editionId: string;
  target: MergeRow;
  cards: MergeCard[];
  rows: MergeRow[];
  onClose: (added: boolean) => void;
};

export function CmsBpaMergeAddDialog({
  editionId,
  target,
  cards,
  rows,
  onClose,
}: CmsBpaMergeAddDialogProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const splitQuery = useQuery(splitOpenQuery(editionId));
  const checkFailed = splitQuery.isError && !splitQuery.isFetching;
  const checkLoading = !splitQuery.isSuccess && !checkFailed;

  const candidates = splitQuery.isSuccess
    ? mergeAddCandidates({ target, rows, splitOpenIds: splitQuery.data })
    : [];
  const picked = candidates.find(row => row.id === pickedId) ?? null;
  const inMerge = cards.filter(card => !card.archived);

  const addMutation = useMutation({
    mutationFn: (added: MergeRow) =>
      researchCms.scheduleMerge(target.id, mergeAddSourceIds(cards, added.id)),
    onSuccess: () => onClose(true),
    onError: err => setError(messageOf(err)),
  });
  const adding = addMutation.isPending;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !adding) onClose(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, adding]);

  function requestClose() {
    if (!adding) onClose(false);
  }

  function handleAdd() {
    if (!picked || adding) return;
    setError(null);
    addMutation.mutate(picked);
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={event => event.target === event.currentTarget && requestClose()}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            Add a practice area to the merge into {target.bpaName}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            aria-label="Close"
            disabled={adding}
            onClick={requestClose}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>

        <section aria-label="Practice areas already in the merge">
          <ul className={styles.list}>
            {inMerge.map(card => (
              <li key={card.editionBpaId} className={styles.row}>
                <span className={styles.rowName}>{card.name}</span>
              </li>
            ))}
          </ul>
        </section>

        <div className={styles.picker} data-empty={picked ? undefined : ""}>
          <Select
            id="merge-add-practice-area"
            label="Practice area to add"
            fullWidth
            value={picked?.id ?? ""}
            disabled={adding || checkLoading}
            onChange={event => setPickedId(event.target.value || null)}
            options={[
              { value: "", label: PICKER_PLACEHOLDER },
              ...candidates.map(row => ({ value: row.id, label: row.bpaName })),
            ]}
          />
        </div>

        {checkFailed && (
          <div className={styles.problem} role="alert">
            <span>
              Could not check which practice areas are being split, so none can be added yet.
            </span>
            <Button
              variant="outlined"
              color="default"
              size="small"
              onClick={() => splitQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {error && (
          <p className={styles.problem} role="alert">
            {error}
          </p>
        )}

        <div className={styles.footer}>
          {checkLoading && (
            <span className={styles.reason} role="status">
              Loading the edition's practice areas...
            </span>
          )}
          <Button
            className={shared.compactButton}
            variant="outlined"
            color="default"
            disabled={adding}
            onClick={requestClose}
          >
            Cancel
          </Button>
          <Button
            className={shared.compactButton}
            variant="contained"
            color="primary"
            disabled={!picked || adding}
            onClick={handleAdd}
          >
            {adding ? "Adding..." : "Add to merge"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
